import React, { useEffect, useMemo, useState, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { getAccountById, getAccountPatients } from "../api";
import {
  getUnallocatedERAsForAccount,
  markERAClaimAllocated,
  markERAClaimDismissed,
} from "../afinanceapi";
import { EditAccount } from "../Components/PatientsAccounts/EditAccounts";
import { NotesHistory } from "../Components/PatientsAccounts/AccountNotes";
import AccountStatements from "../Components/Accounts/AccountStatements";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';

export function Accounts() {
  const { id } = useParams();
  const location = useLocation();

  const [account, setAccount] = useState(null);
  const [dependants, setDependants] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [activeSection, setActiveSection] = useState("account");
  const [unallocatedERAs, setUnallocatedERAs] = useState([]);
  const [allocatingKey, setAllocatingKey] = useState(null);
  const [allocatingAction, setAllocatingAction] = useState(null);
  const navigate = useNavigate();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16).replace("T", " ");
  }, []);

  const hasMedicalAid = useMemo(() => {
    return !!(account?.accountMedicalAid && String(account.accountMedicalAid).trim());
  }, [account]);

  const hasIOD = useMemo(() => {
    return !!(account?.accountIODFund && String(account.accountIODFund).trim());
  }, [account]);

  const canAccessPatients = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Receptionist'].includes(r));
    } catch { return false; }
  }, []);

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!account) return null;

    const fi = (account.accountFullNames || '').charAt(0);
    const accountLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
    const accountCrumb = { label: accountLabel, path: activeSection !== 'account' ? `/accounts/${id}` : undefined };

    let base;

    if (location.state?.fromAppointment) {
      // Appointment > [Date - Name] > [Account Name]
      base = [
        { label: 'Appointments', path: '/bookings' },
        { label: location.state.appointmentLabel, path: location.state.appointmentPath },
        accountCrumb,
      ];
    } else if (location.state?.fromDashboard) {
      // Dashboard > [Patient Name] > [Account Name]
      base = [
        { label: 'Dashboard', path: '/home' },
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.fromPatient) {
      // [Patient Name] > [Account Name]  (no broken /patients link)
      base = [
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.breadcrumbTrail?.length) {
      // Generic upstream trail (e.g. from InvoiceList)
      base = [...location.state.breadcrumbTrail, accountCrumb];
    } else {
      // Accounts > [Account Name]
      base = [
        { label: 'Accounts', path: '/billing', canAccess: canAccessAccounts },
        accountCrumb,
      ];
    }

    return base;
  }, [account, activeSection, id, location.state, canAccessPatients, canAccessAccounts]);

  // ✅ Consistent icon spacing everywhere
  const IconLine = ({ icon, children, className = "" }) => (
    <div className={`d-flex align-items-start mb-1 ${className}`}>
      <i className={`${icon} text-primary me-2 mt-1`} />
      <div className="lh-sm">{children}</div>
    </div>
  );

  const getStatusClass = (status) =>
    status?.toLowerCase().includes("active") ? "text-success" : "text-danger";

  // ✅ Load account
  useEffect(() => {
    async function getAccountInfo() {
      try {
        const accountData = await getAccountById(id);
        setAccount(accountData);
        console.log("📥 Account fetched:", accountData);
      } catch (error) {
        console.error("Error fetching account data:", error);
      }
    }
    getAccountInfo();
  }, [id]);

  // ✅ Load dependants (patients on account)
  useEffect(() => {
    async function loadDependants() {
      try {
        if (!account?._id) return;
        const dependantsData = await getAccountPatients(id);
        setDependants(Array.isArray(dependantsData) ? dependantsData : []);
        console.log("📥 Dependants fetched:", dependantsData);
      } catch (error) {
        console.error("Error fetching dependants data:", error);
        setDependants([]);
      }
    }
    loadDependants();
  }, [id, account?._id]);

  // ✅ Load unallocated ERAs for the account (Finance/SuperAdmin only).
  // The backend joins on accountReference (era:Reference) with a fallback on
  // accountMedicalAidNumber for older ERAs.
  useEffect(() => {
    async function loadUnallocatedERAs() {
      if (!account?._id || !canAccessAccounts) {
        setUnallocatedERAs([]);
        return;
      }
      try {
        const data = await getUnallocatedERAsForAccount(account._id);
        setUnallocatedERAs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching unallocated ERAs:", error);
        setUnallocatedERAs([]);
      }
    }
    loadUnallocatedERAs();
  }, [account?._id, canAccessAccounts]);

  // Build a stable key per row — schemeClaimId may repeat across ERAs but is
  // unique within an eraLog, so combine both.
  const rowKey = (era) => `${era.eraLogId}|${era.schemeClaimId}|${era.treatmentDate || ""}`;

  const handleResolveERA = async (era, action) => {
    const key = rowKey(era);
    setAllocatingKey(key);
    setAllocatingAction(action);
    try {
      const fn = action === "dismiss" ? markERAClaimDismissed : markERAClaimAllocated;
      // Show the spinner for at least 2s so the user sees confirmation before
      // the row vanishes. API call runs in parallel — total wait is max(API, 2s).
      await Promise.all([
        fn({
          eraLogId: era.eraLogId,
          schemeClaimId: era.schemeClaimId,
          treatmentDate: era.treatmentDate,
        }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      setUnallocatedERAs((prev) => prev.filter((e) => rowKey(e) !== key));
    } catch (error) {
      console.error(`Failed to ${action} ERA:`, error);
      alert(`Failed to ${action} ERA. Please try again.`);
    } finally {
      setAllocatingKey(null);
      setAllocatingAction(null);
    }
  };

  function renderAccountInfo() {
    if (!account?._id) return <p>Loading account information...</p>;

    return (
      <div>
        <Row className="align-items-center mb-3">
          <Col md={12}>
            {/* Heading row — account name on the left, "Unallocated ERAs"
                heading on the right, sharing one horizontal row. */}
            <Row className="mb-1">
              <Col md={4}>
                <h6 className="text-primary mb-0">
                  {account?.accountTitle} {account?.accountFullNames}{" "}
                  {account?.accountLastName} ({account?.accountFirstName})
                </h6>
              </Col>
              <Col md={8}>
                {canAccessAccounts && (
                  <h6 className="text-primary mb-0">Unallocated ERAs</h6>
                )}
              </Col>
            </Row>

            <Row>
              {/* Column 1 — contact details under the account name. */}
              <Col md={4}>
                {account?.accountNumber && (
                  <IconLine icon="fa-regular fa-hashtag">
                    {account.accountNumber}
                  </IconLine>
                )}
                {account?.accountIdNumber && (
                  <IconLine icon="fa-solid fa-id-card">
                    {account.accountIdNumber}
                  </IconLine>
                )}
                {account?.accountTelNumberCell && (
                  <IconLine icon="fas fa-phone-alt">
                    {account.accountTelNumberCell}
                  </IconLine>
                )}
                {account?.accountEmail && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail}</IconLine>
                )}
                {account?.accountEmail2 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail2}</IconLine>
                )}
                {account?.accountEmail3 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail3}</IconLine>
                )}
                {account?.accountEmail4 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail4}</IconLine>
                )}
                {account?.accountEmail5 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail5}</IconLine>
                )}
              </Col>

              {/* Columns 2+3 combined — Finance: unallocated ERAs table.
                  Non-finance: employment details. Address has been removed
                  in favour of a wider ERA table. */}
              <Col md={8}>
                {canAccessAccounts ? (
                  <>
                    {unallocatedERAs.length === 0 ? (
                      <div className="text-muted small">None outstanding.</div>
                    ) : (
                      <div style={{ maxHeight: "165px", overflowY: "auto" }}>
                        <Table size="sm" bordered className="mb-0 small">
                          <thead
                            className="table-primary"
                            style={{ position: "sticky", top: 0, zIndex: 1 }}
                          >
                            <tr>
                              <th
                                className="text-primary"
                                style={{ width: "9%" }}
                              >
                                Treatment
                              </th>
                              <th
                                className="text-primary"
                                style={{ width: "7%" }}
                              >
                                Tariff
                              </th>
                              <th
                                className="text-primary"
                                style={{ width: "21%" }}
                              >
                                Reason
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Claimed
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Practice
                              </th>
                              <th
                                className="text-primary text-end"
                                style={{ width: "15%", whiteSpace: "nowrap" }}
                              >
                                Patient
                              </th>
                              <th
                                className="text-primary text-center"
                                style={{ width: "9%" }}
                              >
                                Alloc
                              </th>
                              <th
                                className="text-primary text-center"
                                style={{ width: "9%" }}
                              >
                                Dism
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {unallocatedERAs.map((era) => {
                              const key = rowKey(era);
                              const lines = Array.isArray(era.lines) ? era.lines : [];
                              const totalClaimed = lines.reduce(
                                (s, l) => s + Number(l?.claimedAmount || 0),
                                0
                              );
                              const totalPractice = lines.reduce(
                                (s, l) => s + Number(l?.paidAmount || 0),
                                0
                              );
                              const totalPatient = lines.reduce(
                                (s, l) => s + Number(l?.patientPortion || 0),
                                0
                              );
                              const headerLabel =
                                era.patientName ||
                                [era.eraDate, era.medicalAidName].filter(Boolean).join(" • ") ||
                                "(claim)";
                              return (
                                <React.Fragment key={key}>
                                  <tr className="table-secondary">
                                    <td colSpan={3} className="fw-semibold">
                                      <span title={headerLabel}>{headerLabel}</span>
                                      {era.isNew && (
                                        <span
                                          className="badge rounded-pill bg-success ms-2"
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          New
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalClaimed.toFixed(2)}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalPractice.toFixed(2)}
                                    </td>
                                    <td
                                      className="text-end fw-semibold"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      R {totalPatient.toFixed(2)}
                                    </td>
                                    <td className="text-center">
                                      {allocatingKey === key && allocatingAction === "allocate" ? (
                                        <Spinner
                                          animation="border"
                                          variant="primary"
                                          size="sm"
                                          role="status"
                                          aria-label="Allocating"
                                        />
                                      ) : (
                                        <Form.Check
                                          type="checkbox"
                                          aria-label="Mark claim allocated"
                                          title="Mark claim allocated"
                                          checked={false}
                                          disabled={allocatingKey === key}
                                          onChange={() => handleResolveERA(era, "allocate")}
                                        />
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {allocatingKey === key && allocatingAction === "dismiss" ? (
                                        <Spinner
                                          animation="border"
                                          variant="primary"
                                          size="sm"
                                          role="status"
                                          aria-label="Dismissing"
                                        />
                                      ) : (
                                        <Form.Check
                                          type="checkbox"
                                          aria-label="Dismiss claim"
                                          title="Dismiss claim"
                                          checked={false}
                                          disabled={allocatingKey === key}
                                          onChange={() => handleResolveERA(era, "dismiss")}
                                        />
                                      )}
                                    </td>
                                  </tr>
                                  {lines.length === 0 ? (
                                    <tr>
                                      <td colSpan={8} className="text-muted text-center">
                                        No line detail
                                      </td>
                                    </tr>
                                  ) : (
                                    lines.map((line, i) => {
                                      const reason =
                                        Array.isArray(line.reasonCodes) && line.reasonCodes.length
                                          ? line.reasonCodes
                                              .map((r) =>
                                                [r.code, r.description].filter(Boolean).join(" - ")
                                              )
                                              .join("; ")
                                          : "";
                                      return (
                                        <tr key={`${key}|line-${i}`}>
                                          <td>{line.dateOfService || ""}</td>
                                          <td>{line.tariffCode || ""}</td>
                                          <td title={reason}>{reason}</td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.claimedAmount || 0).toFixed(2)}
                                          </td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.paidAmount || 0).toFixed(2)}
                                          </td>
                                          <td
                                            className="text-end"
                                            style={{ whiteSpace: "nowrap" }}
                                          >
                                            R {Number(line.patientPortion || 0).toFixed(2)}
                                          </td>
                                          <td />
                                          <td />
                                        </tr>
                                      );
                                    })
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {account?.accountOccupation && (
                      <IconLine icon="fas fa-briefcase">{account.accountOccupation}</IconLine>
                    )}
                    {account?.accountEmployer && (
                      <IconLine icon="fas fa-building">{account.accountEmployer}</IconLine>
                    )}
                    {account?.accountTelNumberWork && (
                      <IconLine icon="fas fa-phone-alt">{account.accountTelNumberWork}</IconLine>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={7} className="d-flex justify-content-start gap-2 w-25">
            <EditAccount account={account} show={modalShow} />
            <NotesHistory show={modalShow} account={account} />
          </Col>
        </Row>
      </div>
    );
  }

  function renderCoverInformation() {
    if (!account?._id) return <p>Loading account information...</p>;

    // ✅ Title rules you asked for
    const sectionTitle = hasIOD
      ? "Injury on Duty Information"
      : hasMedicalAid
      ? "Medical Aid Information"
      : "Private Account Information";

    // ✅ Sort dependants numerically by dependant code (only meaningful for medical aid)
    const sortedDependants = [...dependants].sort((a, b) => {
      const aCode = parseInt(a.patientMedicalAidDependantCode || "0", 10);
      const bCode = parseInt(b.patientMedicalAidDependantCode || "0", 10);
      return aCode - bCode;
    });

    return (
      <div>
        <Row className="mb-1">
          <Col md={10}>
            <Row>
              {/* LEFT: Cover info */}
              <Col md={4}>
                <h6 className="text-primary">{sectionTitle}</h6>

                {/* ✅ IOD */}
                {hasIOD && (
                  <>
                    <IconLine icon="fa-solid fa-briefcase-medical">
                      <strong>IOD Fund:</strong> {account.accountIODFund}
                    </IconLine>

                    {(account.accountIODPlan || account.accountIODOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountIODPlan || "-"}
                        {account.accountIODPlan && account.accountIODOption ? " | " : ""}
                        {account.accountIODOption || ""}
                      </div>
                    )}

                    {account.accountIODClaimRefNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Claim Ref #:</strong> {account.accountIODClaimRefNumber}
                      </IconLine>
                    )}

                    {account.accountIODEmployer && (
                      <IconLine icon="fa-solid fa-building">
                        <strong>Employer:</strong> {account.accountIODEmployer}
                      </IconLine>
                    )}

                    {account.accountIODEmployerWCARegNum && (
                      <IconLine icon="fa-solid fa-id-card-clip">
                        <strong>Employer WCA Reg #:</strong>{" "}
                        {account.accountIODEmployerWCARegNum}
                      </IconLine>
                    )}

                    {account.accountIODDateOfInjury && (
                      <IconLine icon="fa-solid fa-calendar-days">
                        <strong>Date of Injury:</strong> {account.accountIODDateOfInjury}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Medical Aid */}
                {!hasIOD && hasMedicalAid && (
                  <>
                    <IconLine icon="fa fa-notes-medical">
                      <strong>Medical Aid:</strong> {account.accountMedicalAid}
                    </IconLine>

                    {(account.accountMedicalAidPlan || account.accountMedicalAidOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountMedicalAidPlan || "-"}
                        {account.accountMedicalAidPlan && account.accountMedicalAidOption
                          ? " | "
                          : ""}
                        {account.accountMedicalAidOption || ""}
                      </div>
                    )}

                    {account.accountMedicalAidNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Number:</strong> {account.accountMedicalAidNumber}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Private */}
                {!hasIOD && !hasMedicalAid && (
                  <div className="text-muted small">
                    No Medical Aid or IOD cover captured for this account.
                  </div>
                )}
              </Col>

              {/* RIGHT: Dependants (only meaningful when Medical Aid exists) */}
              <Col md={8}>
                <h6 className="text-primary">Dependants</h6>

                {sortedDependants.length > 0 ? (
                  <Row as="ul" className="list-unstyled">
                    {sortedDependants.map((p) => (
                      <Row
                        as="li"
                        key={p?._id}
                        className="d-flex align-items-start mb-1"
                      >
                        <Col md={4}>
                          <div
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              // Build trail: upstream ancestors + account (always with path)
                              const fi = (account.accountFullNames || '').charAt(0);
                              const acctLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
                              const ancestors = breadcrumbItems ? breadcrumbItems.slice(0, -1) : [];
                              const trail = [...ancestors, { label: acctLabel, path: `/accounts/${id}` }];
                              navigate(`/patients/appointment/${p._id}/1`, { state: { breadcrumbTrail: trail } });
                            }}
                          >
                            {p?.patientMedicalAidDependantCode
                              ? `${p.patientMedicalAidDependantCode} - `
                              : ""}
                            {p?.patientFirstName} {p?.patientLastName}
                          </div>
                        </Col>

                        {/* ✅ Only show these when Medical Aid exists on the account */}
                        {hasMedicalAid && (
                          <>
                            <Col md={4}>
                              <div className={getStatusClass(p?.patientMedicalAidStatus)}>
                                {p?.patientMedicalAidStatus || ""}
                                {p?.patientMedicalAidJoinDate
                                  ? ` ${p.patientMedicalAidJoinDate}`
                                  : ""}
                              </div>
                            </Col>

                            <Col md={4} className="text-start">
                              <div className="text-muted">
                                checked: {formatDate(p?.patientMedicalAidLastCheck)}
                              </div>
                            </Col>
                          </>
                        )}
                      </Row>
                    ))}
                  </Row>
                ) : (
                  <p className="text-muted mb-0">No associated patients found.</p>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginTop: "65px", position: "relative" }} className="ms-3">
        {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
        <div
          className="container-fluid px-4 px-md-1 bg-white pt-2"
          style={{ maxWidth: "1400px" }}
        >
          {/* Tabs */}
          <Row className="d-flex justify-content-between align-items-center mb-2">
            <Col md={10}>
              <div className="d-flex align-items-center">
                <h5
                  className={`fw-normal d-inline me-3 ${
                    activeSection === "account"
                      ? "text-primary"
                      : "text-secondary fw-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveSection("account")}
                >
                  Account Information
                </h5>
                <span className="text-secondary mx-3">|</span>
                <h5
                  className={`fw-normal d-inline ${
                    activeSection === "medicalAid"
                      ? "text-primary"
                      : "text-secondary fw-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveSection("medicalAid")}
                >
                  {hasIOD
                    ? "Injury on Duty Information"
                    : hasMedicalAid
                    ? "Medical Aid Information"
                    : "Private Account Information"}
                </h5>
              </div>
            </Col>
          </Row>

          {/* Content */}
          <Row className="ms-1 mb-1">
            {activeSection === "account" && renderAccountInfo()}
            {activeSection === "medicalAid" && renderCoverInformation()}
          </Row>
        </div>
      </div>

      <hr />

      <Row className="ms-1">
        <AccountStatements account={account} breadcrumbContext={location.state} />
      </Row>
    </div>
  );
}
